import type { OperationsNode } from '../types/operations.types'
import { isTeamNode } from './nodeTypes'
import { findNodeParent } from './findNodeParent'

export type DropHint = 'before' | 'after' | 'into'

const findNode = (
  nodes: OperationsNode[],
  id: string
): OperationsNode | null => {
  for (const node of nodes) {
    if (node.id === id) {
      return node
    }
    const found = findNode(node.children, id)
    if (found) {
      return found
    }
  }
  return null
}

/**
 * What to draw on the row currently under the pointer, mid-drag.
 *
 * `into` when hovering a division row — the whole row lights up, because the
 * team joins that division rather than landing at a specific slot. `before` /
 * `after` when hovering a team, showing the exact insertion line.
 *
 * Returns null when the hover is not a legal drop (a conference, the season, or
 * the dragged row itself), so nothing is drawn and the gesture reads as refused.
 */
export const resolveDropHint = (
  nodes: OperationsNode[],
  activeId: string | null,
  overId: string | null,
  /** The pointer's slot — below the hovered row rather than above it. */
  after = false
): DropHint | null => {
  if (!activeId || !overId) {
    return null
  }

  const dragged = findNode(nodes, activeId)
  const over = findNode(nodes, overId)
  if (!dragged || !over || !isTeamNode(dragged)) {
    return null
  }

  if (!isTeamNode(over)) {
    // Only a division can receive a team; conferences and the season cannot.
    return over.type === 'division' ? 'into' : null
  }

  if (!findNodeParent(nodes, overId)) {
    return null
  }

  // The slot IS the answer — no inference. Whatever line the user sees is the
  // gap the team drops into.
  return after ? 'after' : 'before'
}
